import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import AppHeader from '../components/layout/AppHeader';
import CouponHowToUse from '../components/bazzar/CouponHowToUse';
import MyRewardPageHeader from '../components/bazzar/MyRewardPageHeader';
import RapchikRewardDetailPanel from '../components/bazzar/RapchikRewardDetailPanel';
import { fetchRapchikRewardById } from '../services/bazaarService';
import { clearAuthSession } from '../storage/authStorage';
import { MY_REWARD_PATH } from '../routes/paths';

export default function RapchikRewardDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const loadReward = useCallback(async () => {
    setLoading(true);

    try {
      const item = await fetchRapchikRewardById(id);
      setReward(item);
    } catch {
      setReward(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReward();
  }, [loadReward]);

  const handleConfirmLogout = () => {
    clearAuthSession();
    window.location.href = '/';
  };

  return (
    <MainLayout>
      <Box sx={{ px: { md: '50px' } }}>
        <AppHeader endVariant="profile" onLogoutClick={() => setLogoutDialogOpen(true)} />
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: 6, pt: { xs: 1, md: 2 } }}>
        <MyRewardPageHeader
          title="Coupon Info"
          backLabel="Back to My Rewards"
          backTo={MY_REWARD_PATH}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#ff00f2' }} />
          </Box>
        ) : !reward ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
            <Typography sx={{ color: '#e6d4ff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}>
              Reward not found.
            </Typography>
            <Button
              onClick={() => navigate(MY_REWARD_PATH)}
              sx={{ color: '#ff00f2', textTransform: 'none' }}
            >
              Back to My Rewards
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, lg: 5 },
              maxWidth: 960,
              mx: 'auto',
              alignItems: 'start',
            }}
          >
            <RapchikRewardDetailPanel reward={reward} />
            <CouponHowToUse steps={reward.howToUseSteps} />
          </Box>
        )}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: '#e6d4ff', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
